// src/pages/PhotosPage.tsx

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useLazyThumbnail } from '../hooks/useLazyThumbnail';
import type { PhotoItem, AwsCredentials } from '../models/types';

/**
 * ThumbnailItem — renders a single thumbnail with lazy loading via IntersectionObserver.
 * Generates the pre-signed URL only when the item enters the viewport.
 */
function ThumbnailItem({
  photo,
  index,
  credentials,
  onClick,
}: {
  photo: PhotoItem;
  index: number;
  credentials: AwsCredentials | null;
  onClick: () => void;
}) {
  const { ref, thumbnailUrl, isLoading, hasError, retry } = useLazyThumbnail(
    photo.key,
    credentials
  );
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const showPlaceholder = !thumbnailUrl || isLoading || (!imgLoaded && !imgError && !hasError);
  const showError = hasError || imgError;

  return (
    <div
      ref={ref}
      className="thumbnail-item"
      role="button"
      tabIndex={0}
      aria-label={`Photo ${photo.filename}`}
      onClick={showError ? undefined : onClick}
      onKeyDown={(e) => {
        if (!showError && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      style={{ minHeight: '44px', minWidth: '44px' }}
    >
      {showError ? (
        <div
          className="thumbnail-error"
          onClick={(e) => {
            e.stopPropagation();
            setImgError(false);
            retry();
          }}
          role="button"
          tabIndex={0}
          aria-label={`Retry loading ${photo.filename}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setImgError(false);
              retry();
            }
          }}
          style={{ minHeight: '44px', minWidth: '44px' }}
        >
          <span className="broken-image-icon" aria-hidden="true">⚠</span>
          <span className="retry-text">Tap to retry</span>
        </div>
      ) : (
        <>
          {showPlaceholder && (
            <div
              className="thumbnail-placeholder"
              aria-hidden="true"
              style={{
                backgroundColor: `hsl(${(index * 37) % 360}, 40%, 80%)`,
              }}
            />
          )}
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt={photo.filename}
              loading="lazy"
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              style={{ opacity: imgLoaded ? 1 : 0, position: imgLoaded ? 'static' : 'absolute' }}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * ThumbnailGrid — renders the responsive grid of thumbnail items.
 */
function ThumbnailGrid({
  photos,
  credentials,
  onPhotoClick,
  pageOffset,
}: {
  photos: PhotoItem[];
  credentials: AwsCredentials | null;
  onPhotoClick: (index: number) => void;
  pageOffset: number;
}) {
  return (
    <div className="thumbnail-grid" role="list" aria-label="Photo thumbnails">
      {photos.map((photo, i) => (
        <ThumbnailItem
          key={photo.key}
          photo={photo}
          index={i}
          credentials={credentials}
          onClick={() => onPhotoClick(pageOffset + i)}
        />
      ))}
    </div>
  );
}

/**
 * PhotosPage — displays thumbnails for an event with pagination,
 * lazy loading, and navigation controls.
 */
export function PhotosPage() {
  const { year, event } = useParams<{ year: string; event: string }>();
  const navigate = useNavigate();

  const photos = useAppStore((s) => s.photos);
  const isLoading = useAppStore((s) => s.isLoading);
  const error = useAppStore((s) => s.error);
  const fetchPhotos = useAppStore((s) => s.fetchPhotos);
  const awsCredentials = useAppStore((s) => s.awsCredentials);

  const [currentPage, setCurrentPage] = useState(1);

  // Fetch photos on mount and when page changes
  useEffect(() => {
    if (year && event) {
      fetchPhotos(year, event, currentPage);
    }
  }, [year, event, currentPage, fetchPhotos]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePhotoClick = (index: number) => {
    navigate(`/years/${year}/${event}/${index}`);
  };

  const handleRefresh = () => {
    if (year && event) {
      fetchPhotos(year, event, currentPage);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="photos-page" role="status" aria-label="Loading photos">
        <div className="loading-indicator">Loading…</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="photos-page">
        <div className="error-state" role="alert">
          <p>{error}</p>
          <button
            className="retry-button"
            onClick={handleRefresh}
            style={{ minHeight: '44px' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!photos || photos.photos.length === 0) {
    return (
      <div className="photos-page">
        <div className="photos-header">
          <button
            className="back-button"
            onClick={() => navigate(`/years/${year}`)}
            aria-label="Back to events"
            style={{ minHeight: '44px' }}
          >
            ← Back
          </button>
          <h1>{event}</h1>
          <button
            className="refresh-button"
            onClick={handleRefresh}
            aria-label="Refresh photos"
            style={{ minHeight: '44px' }}
          >
            Refresh
          </button>
        </div>
        <p className="empty-state">No photos in this event</p>
      </div>
    );
  }

  // Calculate page offset for absolute index in the event
  const pageOffset = (photos.currentPage - 1) * 50;

  return (
    <div className="photos-page">
      <div className="photos-header">
        <button
          className="back-button"
          onClick={() => navigate(`/years/${year}`)}
          aria-label="Back to events"
          style={{ minHeight: '44px' }}
        >
          ← Back
        </button>
        <h1>{event}</h1>
        <button
          className="refresh-button"
          onClick={handleRefresh}
          aria-label="Refresh photos"
          style={{ minHeight: '44px' }}
        >
          Refresh
        </button>
      </div>

      <ThumbnailGrid
        photos={photos.photos}
        credentials={awsCredentials}
        onPhotoClick={handlePhotoClick}
        pageOffset={pageOffset}
      />

      {/* Pagination controls */}
      {photos.totalPages > 1 && (
        <div className="pagination-controls" aria-label="Pagination">
          <button
            className="pagination-prev"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            aria-label="Previous page"
            style={{ minHeight: '44px' }}
          >
            ← Prev
          </button>
          <span className="pagination-info">
            Page {photos.currentPage} of {photos.totalPages}
          </span>
          <button
            className="pagination-next"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= photos.totalPages}
            aria-label="Next page"
            style={{ minHeight: '44px' }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
